import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as FirebaseUser, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { AppUser } from '../types/user';
import { isEmailVerified } from '../utils/emailVerification';

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  emailVerified: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  emailVerified: false,
  refreshUser: async () => {},
});

export const useAuthContext = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

const USER_CACHE_KEY = 'auth_user_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedUser {
  user: AppUser;
  timestamp: number;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Try to load cached user data
  const getCachedUser = (): AppUser | null => {
    try {
      const cached = sessionStorage.getItem(USER_CACHE_KEY);
      if (!cached) return null;
      
      const data: CachedUser = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if ((now - data.timestamp) < CACHE_DURATION) {
        console.log('[AuthContext] Using cached user data');
        return data.user;
      }
      
      // Clear expired cache
      sessionStorage.removeItem(USER_CACHE_KEY);
      return null;
    } catch {
      return null;
    }
  };
  
  const cachedUser = getCachedUser();
  
  const [user, setUser] = useState<AppUser | null>(cachedUser);
  const [loading, setLoading] = useState(!cachedUser);
  const [emailVerified, setEmailVerified] = useState(cachedUser?.emailVerified || false);

  const refreshUser = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;

    try {
      console.log('[AuthContext] Refreshing user data from Firestore...');
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = { ...(userDoc.data() as AppUser), uid: firebaseUser.uid };
        setUser(userData);
        console.log('[AuthContext] User data refreshed:', {
          email: userData.email,
          subscriptionTier: userData.subscriptionTier,
          plan: userData.plan
        });
      }
    } catch (error) {
      console.error('[AuthContext] Error refreshing user data:', error);
    }
  };

  useEffect(() => {
    // Auth persistence is already set in firebase.ts
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      console.log('[AuthContext] Auth state changed:', { 
        hasUser: !!firebaseUser, 
        email: firebaseUser?.email 
      });
      
      if (firebaseUser) {
        try {
          // Reload user to get latest emailVerified status from Firebase
          await firebaseUser.reload();
          const verified = isEmailVerified(auth.currentUser);
          setEmailVerified(verified);
          
          console.log('[AuthContext] Email verification status:', 
            'emailVerified:', auth.currentUser?.emailVerified,
            'isVerified:', verified,
            'provider:', auth.currentUser?.providerData[0]?.providerId
          );
          
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = { ...(userDoc.data() as AppUser), uid: firebaseUser.uid };
            
            const updates: any = {};
            
            if (!userData.email) {
              updates.email = firebaseUser.email;
            }
            
            if (!userData.createdAt) {
              updates.createdAt = new Date();
            }
            
            if (!userData.storageUsedBytes) {
              updates.storageUsedBytes = 0;
            }
            
            // NOTE: storageLimitGB is NO LONGER stored in Firestore
            // It's calculated dynamically from plan using getStorageLimitGB()
            // Remove storageLimitGB field if it exists (migration)
            if (userData.storageLimitGB !== undefined) {
              console.log(`[AuthContext] Removing deprecated storageLimitGB field`);
              // Will be handled by migration script
            }
            
            const currentTier = userData.subscriptionTier || userData.plan || 'free';
            
            if (!userData.plan) {
              updates.plan = 'free';
            }
            
            if (!userData.subscriptionTier) {
              updates.subscriptionTier = currentTier;
            }
            
            // Ensure all users are instructors (EduMi is for instructors only)
            if (!userData.role || userData.role !== 'instructor') {
              console.log('[AuthContext] Setting role to instructor:', firebaseUser.email);
              updates.role = 'instructor';
            }
            
            // Lazy assignment: Update userType for teachers with paid subscriptions
            const hasPaidSubscription = currentTier && currentTier !== 'free';
            if (hasPaidSubscription && !userData.userType) {
              updates.userType = 'teacher';
              console.log('[AuthContext] Setting userType to: teacher');
            }
            
            if (Object.keys(updates).length > 0) {
              console.log('[AuthContext] Updating user with:', updates);
              await updateDoc(userDocRef, updates);
              Object.assign(userData, updates);
              console.log('[AuthContext] User updated successfully, role:', userData.role);
            }
            
            console.log('[AuthContext] Final user data:', { email: userData.email, role: userData.role });
            
            // Cache user data in sessionStorage
            const cacheData: CachedUser = {
              user: userData,
              timestamp: Date.now()
            };
            sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(cacheData));
            
            setUser(userData);
          } else {
            const newUser: AppUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || '',
              role: 'instructor',
              plan: 'free',
              subscriptionTier: 'free',
              storageUsedBytes: 0,
              createdAt: new Date(),
            };
            
            await setDoc(userDocRef, newUser);
            
            // Cache new user data
            const cacheData: CachedUser = {
              user: newUser,
              timestamp: Date.now()
            };
            sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(cacheData));
            
            setUser(newUser);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(null);
          setEmailVerified(false);
        }
      } else {
        console.log('[AuthContext] No Firebase user, setting user to null');
        // Clear cache on logout
        sessionStorage.removeItem(USER_CACHE_KEY);
        // เคลียร์ single-session markers ตอน logout → ครั้งหน้าที่ login จะยึด session ใหม่ได้สะอาด (ไม่โดนเตะเอง)
        try {
          Object.keys(localStorage).filter((k) => k.startsWith('cd_session_')).forEach((k) => localStorage.removeItem(k));
        } catch {}
        setUser(null);
        setEmailVerified(false);
      }
      
      console.log('[AuthContext] Setting loading to false');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Periodic check for email verification (every 5 seconds)
  useEffect(() => {
    if (!user || emailVerified) return;

    const interval = setInterval(async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        const verified = isEmailVerified(auth.currentUser);
        if (verified !== emailVerified) {
          console.log('[AuthContext] Email verification status changed:', verified);
          setEmailVerified(verified);
          
          // Update Firestore to trigger Cloud Function
          if (verified) {
            try {
              const userDocRef = doc(db, 'users', auth.currentUser.uid);
              await updateDoc(userDocRef, {
                emailVerified: true,
                updatedAt: new Date()
              });
              console.log('[AuthContext] ✅ Updated emailVerified in Firestore');
            } catch (error) {
              console.error('[AuthContext] Error updating emailVerified:', error);
            }
          }
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [user, emailVerified]);

  // 🔒 Single-session: อนุญาตให้ login ได้ทีละอุปกรณ์เท่านั้น
  // ⚠️ ปิดไว้ชั่วคราว (2026-08-27): เรามีหลายโดเมน (coursedee.web.app / aiforyou.web.app / edumi ฯลฯ)
  //    ที่ใช้ Firestore users doc เดียวกัน แต่ localStorage แยกกันต่อ origin → แต่ละโดเมนยึด session ใหม่
  //    ทำให้ "เตะกันเองไปมา" ไม่จบ (ผู้ใช้ถูก logout รัว ๆ). ถ้าจะเปิดใหม่ต้องออกแบบให้ทนหลาย origin ก่อน.
  const SINGLE_SESSION_ENABLED = false;
  useEffect(() => {
    if (!SINGLE_SESSION_ENABLED) return;
    const uid = user?.uid;
    if (!uid) return;
    const key = `cd_session_${uid}`;
    let cancelled = false;
    let unsub: (() => void) | null = null;

    (async () => {
      let localSid = localStorage.getItem(key);
      if (!localSid) {
        // ไม่มี session ในเครื่องนี้ = ถือเป็นการ login ใหม่ → ยึด session (เตะเครื่องอื่นออก)
        localSid = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(key, localSid);
        try {
          await setDoc(doc(db, 'users', uid), {
            activeSessionId: localSid,
            activeSessionAt: serverTimestamp(),
            activeSessionDevice: (navigator.userAgent || '').slice(0, 200),
          }, { merge: true });
        } catch (e) {
          console.error('[Session] claim error', e);
        }
      }
      if (cancelled) return;
      // เฝ้าดู activeSessionId — ถ้าเปลี่ยนเป็นของอุปกรณ์อื่น = โดน login ที่อื่นทับ → ออกจากระบบ
      unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
        if (cancelled) return;
        const remote = (snap.data() as any)?.activeSessionId;
        if (remote && remote !== localSid) {
          cancelled = true;
          localStorage.removeItem(key);
          try { window.alert('บัญชีนี้มีการเข้าสู่ระบบจากอุปกรณ์อื่น\nคุณจึงถูกออกจากระบบนี้'); } catch {}
          signOut(auth).catch(() => {});
        }
      });
    })();

    return () => { cancelled = true; if (unsub) unsub(); };
  }, [user?.uid]);

  return (
    <AuthContext.Provider value={{ user, loading, emailVerified, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
